<template>
  <div style="overflow-y: auto;">
    <div :style="{ 'min-width': minWidth + 'px' } ">
      <div class="d-flex justify-content-between p-3 top-bar">
        <div
          v-for="(timeInterval, key) in timeIntervals"
          :key="timeInterval"
        >
          <template v-if="key === 0 || key === (timeIntervals.length - 1)">
            {{ timeInterval }}
          </template>
          <template v-else-if="minWidth < 1000 && (key - firstHourInterval) % 4 === 0">
            {{ timeInterval }}
          </template>
          <template v-else-if="minWidth >= 1000 && minWidth < 2000 && (key - firstHourInterval) % 2 === 0">
            {{ timeInterval }}
          </template>
          <template v-else-if="minWidth >= 2000">
            {{ timeInterval }}
          </template>
        </div>
      </div>
      <div
        class="bg-gray"
        @drop.prevent.stop="dragDrop"
        @dragover.prevent="dragover"
        @dragenter.prevent="dragEnter"
        @dragleave.prevent="dragLeave"
      >
        <GridLayout
          ref="gridlayout"
          class="grid-width"
          :layout.sync="dataAssessmentConfigItems"
          :col-num="columnAmount"
          :row-height="rowHeight"
          is-bounded
          :is-resizable="false"
          :is-mirrored="false"
          vertical-compact
          :margin="[10, 10]"
          use-css-transforms
        >
          <div
            v-tooltip="trans('core.add_object', { 'object': trans_choice('core.events', 1) })"
            class="new-event"
            :title="trans('core.add_object', { 'object': trans_choice('core.events', 1) })"
          >
            <button
              class="new-event__button"
              @click="displayCreateEvent = true"
            >
              <span class="fa fa-plus" />
            </button>
          </div>
          <div
            v-for="line in verticalLines"
            :style="{ left: `${line * 100 / columnAmount}%` }"
            class="vertical-line"
          />
          <GridItem
            v-for="dataAssessmentConfigItem in dataAssessmentConfigItems"
            :key="dataAssessmentConfigItem.i"
            :x="dataAssessmentConfigItem.x"
            :y="dataAssessmentConfigItem.y"
            :w="dataAssessmentConfigItem.w"
            :h="dataAssessmentConfigItem.h"
            :i="dataAssessmentConfigItem.i"
            :is-draggable="false"
          >
            <AssessmentConfigItemView
              :assessment-config-item="dataAssessmentConfigItem"
              :assessment-config="assessmentConfig"
              @edit="editSelectedAssessmentConfigItem"
            />
          </GridItem>
        </GridLayout>
      </div>
      <Modal
        v-if="selectedAssessmentConfigItem != null"
        :open="true"
        :title="trans('event.assessment_configs.edit_assessment_config')"
        @close="selectedAssessmentConfigItem = null"
      >
        <AssessmentConfigEventEditView
          :assessment-config="selectedAssessmentConfigItem"
          :parent-event="parentEvent"
          @close="selectedAssessmentConfigItem = null"
        />
      </Modal>
      <Modal
        v-if="displayCreateEvent"
        :open="displayCreateEvent"
        :title="trans('core.add_object', { 'object': trans_choice('core.events', 1) })"
        @close="displayCreateEvent = false"
      >
        <AssessmentConfigEventCreateView
          :parent-event="parentEvent"
          @close="displayCreateEvent = null"
        />
      </Modal>
    </div>
  </div>
</template>

<script>
import AssessmentConfigEventCreateView
  from '@/employees/components/AssessmentConfig/view/AssessmentConfigEventCreateView.vue'
import toast from '@/services/toast'
import { mapState } from 'vuex'
import { GridItem, GridLayout } from 'vue-grid-layout'
import moment from 'moment'
import { extendMoment } from 'moment-range'
import AssessmentConfigItemView from '@/employees/components/AssessmentConfig/view/AssessmentConfigItemView.vue'
import AssessmentConfigEventEditView
  from '@/employees/components/AssessmentConfig/view/AssessmentConfigEventEditView.vue'
import Modal from '@/employees/AttendanceList/components/Modal'

export default {
  name: 'AssessmentConfigGridView',
  components: {
    AssessmentConfigEventCreateView,
    GridItem,
    GridLayout,
    AssessmentConfigItemView,
    AssessmentConfigEventEditView,
    Modal,
  },
  props: {
    parentEvent: {
      type: Object,
      required: true,
    },
    events: {
      type: Array,
      required: true,
    },
    stepMinuteDuration: {
      type: Number,
      required: false,
      default: 5,
    },
  },
  data () {
    return {
      dataAssessmentConfigItems: [],
      selectedAssessmentConfigItem: null,
      displayCreateEvent: false,
      currentEvents: [],
      minWidth: 0,
    }
  },
  computed: {
    ...mapState({
      assessmentConfig (state) {
        return state.events.assessment_configs[this.parentEvent.assessmentConfig]
      },
    }),
    rowHeight () {
      return 80 + this.assessmentConfig.config_data.students_per_timeslot * 40
    },
    /** @typedef {Object} AssessmentConfigItem
     * @property {Number} i
     * @property {Number} x
     * @property {Number} y
     * @property {Number} w
     * @property {Number} h
     * @property {Boolean} locked
     * @property {Object} event
     */
    assessmentConfigItems () {
      /** @type [[AssessmentConfigItem]] */
      const lanes = []
      return this.currentEvents
        .map((event) => {
          /** @type AssessmentConfigItem */
          const assessmentConfigItem = {
            i: Number(event.id),
            ...this.calculateEvent(event),
            h: 1,
            locked: false,
            event,
          }
          assessmentConfigItem.y = this.calculateEventYPosition(assessmentConfigItem, lanes)
          return assessmentConfigItem
        })
    },

    // total minutes in grid
    parentEventMinutDuration () {
      return moment.duration(moment(this.endTime).diff(moment(this.startTime))).asMinutes()
    },

    columnAmount () {
      return this.parentEventMinutDuration / this.stepMinuteDuration
    },
    firstHourInterval () {
      const r = Math.round(parseInt(moment(this.startTime).format('mm')) / 15)

      // If r > 2, then 30+ minutes, we need to take 1 or 2 steps
      if (r > 2) {
        return 4 - r
      }

      // if r < 2, then 0 - 29 so we remove 0 or 1
      return -1 * r
    },
    timeIntervals () {
      const timeIntervals = [this.startTime.format('HH:mm')]

      const timestampAmount = this.parentEventMinutDuration / 15
      const minutes = 15

      const startTime = moment(this.startTime)

      for (let i = 1; i < timestampAmount; i++) {
        startTime.add(minutes, 'minutes')
        timeIntervals.push(startTime.format('HH:mm'))
      }

      timeIntervals.push(moment(this.endTime).format('HH:mm'))
      return timeIntervals
    },
    verticalLines () {
      let i = 0
      const cols = []
      const division = parseInt(15 / this.stepMinuteDuration)
      while (i <= this.columnAmount) {
        if ((i % division === 0 && this.minWidth < 2000) || this.minWidth >= 2000) {
          cols.push(i)
        }

        i = i + 1
      }
      return cols
    },

    startTime () {
      const startTimes = this.events.map(event => event.starttime)
      startTimes.push(this.parentEvent.starttime)
      startTimes.sort((a, b) => moment(a).diff(moment(b)))
      const startTime = moment(startTimes[0])
      startTime.minutes(startTime.minutes() - (startTime.minutes() % 15))

      return startTime
    },

    endTime () {
      const endTimes = this.events.map(event => event.endtime)
      endTimes.push(this.parentEvent.endtime)
      endTimes.sort((a, b) => moment(b).diff(moment(a)))
      const endTime = moment(endTimes[0])
      endTime.minutes(15 * Math.ceil(endTime.minutes() / 15))

      return endTime
    },
  },
  watch: {
    events: {
      handler (newValue) {
        this.currentEvents = this.events
        this.dataAssessmentConfigItems = this.assessmentConfigItems
        this.calculateMinWidth()
      },
      deep: true,
    },
  },
  mounted () {
    this.currentEvents = this.events
    this.dataAssessmentConfigItems = this.assessmentConfigItems
    this.calculateMinWidth()
  },
  methods: {
    editSelectedAssessmentConfigItem (assessmentConfigItem) {
      this.selectedAssessmentConfigItem = assessmentConfigItem
    },
    calculateEvent (event) {
      return {
        w: this.calculateEventDurationWidth(event),
        x: this.calculateEventXPosition(event),
      }
    },
    calculateEventDurationWidth (event) {
      return moment.duration(moment(event.endtime).diff(moment(event.starttime))).asMinutes() / this.stepMinuteDuration
    },
    calculateEventXPosition (event) {
      return moment.duration(moment(event.starttime).diff(moment(this.startTime))).asMinutes() / this.stepMinuteDuration
    },
    /**
     * @param {AssessmentConfigItem} assessmentConfigItem
     * @param {[[AssessmentConfigItem]]} lanes
     *
     * @returns Number
     */
    calculateEventYPosition (assessmentConfigItem, lanes) {
      //* The first item can always be placed on the first lane
      if (lanes.length === 0) {
        lanes.push([])
        lanes[0].push(assessmentConfigItem)
        assessmentConfigItem.y = 0
        return 0
      }

      //* Looping through the lanes
      for (let i = 0; i < lanes.length; i++) {
        let overlap = false
        //* Looping through the items of the lane
        for (let j = 0; j < lanes[i].length; j++) {
          if (this.eventsOverlap(assessmentConfigItem, lanes[i][j])) {
            overlap = true
            break
          }
        }
        //* If nothing overlaps in the lane then this item can be placed in this array
        if (overlap === false) {
          lanes[i].push(assessmentConfigItem)
          return i
        } else if (i + 1 === lanes.length) {
          lanes.push([])
        }
      }
    },
    /**
     * @param {AssessmentConfigItem} assessmentConfigItem1
     * @param {AssessmentConfigItem} assessmentConfigItem2
     *
     * @returns Boolean
     */
    eventsOverlap (assessmentConfigItem1, assessmentConfigItem2) {
      const rangeMoment = extendMoment(moment)
      const assessmentConfigItem1Range = rangeMoment.range(assessmentConfigItem1.event.starttime, assessmentConfigItem1.event.endtime)
      const assessmentConfigItem2Range = rangeMoment.range(assessmentConfigItem2.event.starttime, assessmentConfigItem2.event.endtime)
      return assessmentConfigItem1Range.overlaps(assessmentConfigItem2Range)
    },

    /**
     * Calculates the minWidth of the component to make sure the smallest is still readable
     *
     */

    calculateMinWidth () {
      let minutes = this.parentEventMinutDuration
      const size = this.$el.clientWidth - 35

      const minSize = 120

      for (const event of this.currentEvents) {
        minutes = Math.min(minutes, (moment.duration(moment(event.endtime).diff(moment(event.starttime))).asMinutes()))
      }

      this.minWidth = Math.max(size, this.parentEventMinutDuration / minutes * minSize)
    },
    dragEnter (event) {
      this.isHovering = true
    },
    dragover (event) {
      this.isHovering = true
    },
    dragLeave (event) {
      this.isHovering = false
    },
    dragDrop (event) {
      const payload = JSON.parse(event.dataTransfer.getData('text/plain'))
      if (payload.eventId && payload.studentId) {
        this.$store.dispatch('moveStudentToEvent',
          {
            data: {
              to: null,
              from: payload.eventId,
              student: payload.studentId,
            },
          },
        ).catch((e) => {
          return toast.warning(Lang.get('errors.move_not_possible'))
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.grid-width {
  width: calc(100% - 35px);
  margin-left: 17.5px;
  margin-right: 17.5px;
  z-index: 2;
}

.bg-gray {
  background-color: #eee;
}

.bg-darken {
  background-color: darken(#eee, 1);
}

.top-bar {
  height: 40px;
  background-color: #646B78;
  color: #ffffff;
}

.vertical-line {
  position: absolute;
  top: 0;
  bottom: 0;
  border: gray;
  border-width: 1px;
  border-style: dashed;
  opacity: 80%;
}

.new-event {
  position: absolute;
  right: 15px;
  bottom: 0;
  margin-bottom: 10px;
  z-index: 2;
  border-radius: 35px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 1px 5px 0 rgba(0, 0, 0, 0.12);

  :hover {
    background-color: #25167a !important;
    color: #eee;
  }

  &__button {
    margin: 0;
    height: 35px;
    width: 35px;
    border-radius: 35px;
    display: flex;
    justify-content: center;
    cursor: pointer;
    background-color: #25167a !important;
    border: none;
    color: white;
    flex-direction: column;
    align-items: center;
  }

}
</style>
